import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { ProtectedRoute, TemporaryProtectedRoute } from "./ProtectedRoute";
import { useUserInfo } from "GlobalStates/User";
import { ROUTES } from "./routeConstants";
import { HeroIcon } from "Features/HeroIcon";

// Frequently accessed components - eager load these
import { LandingPage } from "Features/LandingPage/LandingPage";
import { CollegeFinder } from "Features/CollegeFinder/collegeFinder";
import UserDashboard from "Features/UserDashboard/UserDashboard";
import LoginPage from "Features/Auth/LoginPage";

// Lazy load less frequently accessed components
const Pricing = lazy(() => import("Features/Pricing/pricing"));
const Payment = lazy(() => import("Features/Payment/Payment"));
const StripePaymentProcessing = lazy(() =>
  import("Features/Payment/StripePaymentProcessing").then((module) => ({
    default: module.StripePaymentProcessing,
  })),
);
const PhonePePaymentProcessing = lazy(() =>
  import("Features/Payment/PhonePePaymentProcessing").then((module) => ({
    default: module.PhonePePaymentProcessing,
  })),
);
const ReadinessCalculatorForm = lazy(
  () => import("Features/ReadinessCalculator/readinessCalculatorForm"),
);
const CollegeDashboard = lazy(
  () => import("Features/Colleges/collegeDashboard"),
);
const College = lazy(() => import("Features/CollegeCompare/collegeCompare"));
const StudentLoanCalculator = lazy(
  () => import("Features/Calculators/LoanCalculator"),
);
const Finance = lazy(() => import("Features/Finance/Finance"));
const FinanceFlow = lazy(() => import("Features/Finance/FinanceFlow"));
const ConnectOG = lazy(() => import("Features/OGConnect/ConnectOG"));
const LoanEligibility = lazy(
  () => import("Features/Finance/LoanEligibility/LoanEligibility"),
);
const DocumentUpload = lazy(
  () => import("Features/Finance/LoanEligibility/DocumentUpload"),
);
const Scholarship = lazy(() => import("Features/Scholarships"));
const Phone = lazy(() => import("Features/Finance/LoanEligibility/Phone"));
const ChatBot = lazy(() => import("Features/ChatBot/ChatBot"));
const FiltersPage = lazy(() => import("Features/OGConnect/FiltersPage"));
const ProfileDetailsPage = lazy(
  () => import("Features/OGConnect/ProfileDetailsPage/ProfileDetailsPage"),
);
const ProfilesPage = lazy(
  () => import("Features/OGConnect/ProfilesPage/ProfilesPage"),
);
const DemoVideos = lazy(() => import("Features/Demos/Demos"));
const PrivacyPolicy = lazy(() => import("Features/PrivacyPolicy"));
const ProfilePage = lazy(() => import("Features/UserProfile/profile"));
const PaymentFailed = lazy(() =>
  import("Features/Payment/PaymentFailed").then((module) => ({
    default: module.PaymentFailed,
  })),
);
const Blog = lazy(() => import("Features/Blog"));
const SingleBlog = lazy(() => import("Features/Blog/SingleBlog"));
const PublicFinance = lazy(() => import("Features/PublicFinance"));
const OurTeam = lazy(() => import("Features/OurTeam"));
const Community = lazy(() => import("Features/Community"));
const PublicScholarship = lazy(() => import("Features/PublicScholarship"));
const ScheduleFinanceCall = lazy(
  () => import("Features/FinanceCallSchedule/ScheduleFinanceCall"),
);
const VerifyEmail = lazy(() => import("Features/EmailVerification"));
const TermsPolicies = lazy(
  () => import("Features/TermsPolicies/TermsPolicies"),
);

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

export const RouterWrapper = () => {
  const { user } = useUserInfo();

  return (
    <>
      {user?.token && <HeroIcon />}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* public routes */}
          <Route path={ROUTES.HOME} element={<LandingPage />} />
          <Route path={ROUTES.OUR_TEAM} element={<OurTeam />} />
          <Route path={ROUTES.COLLEGE_FINDER} element={<CollegeFinder />} />
          <Route path={ROUTES.DEMOS} element={<DemoVideos />} />
          <Route path={ROUTES.PRICING} element={<Pricing />} />
          <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
          <Route path={ROUTES.BLOG} element={<Blog />} />
          <Route path={ROUTES.BLOG_CATEGORY} element={<Blog />} />
          <Route path={ROUTES.BLOG_SINGLE} element={<SingleBlog />} />
          <Route path={ROUTES.PUBLIC_FINANCE} element={<PublicFinance />} />
          <Route path={ROUTES.COMMUNITY} element={<Community />} />
          <Route
            path={ROUTES.PUBLIC_SCHOLARSHIP}
            element={<PublicScholarship showFinalFooter={true} />}
          />
          <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
          <Route path={ROUTES.TERMS} element={<TermsPolicies />} />
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />

          {/* protected routes */}
          {/* 0. Schedule Finance Call */}
          <Route
            path={ROUTES.SCHEDULE_FINANCE_CALL}
            element={
              <TemporaryProtectedRoute>
                <ScheduleFinanceCall />
              </TemporaryProtectedRoute>
            }
          />

          {/* 1. User Dashboard */}
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            }
          />

          {/* 2. Readiness Calculator (or) My Journey */}
          <Route
            path={ROUTES.READINESS_CALCULATOR}
            element={
              <ProtectedRoute>
                <ReadinessCalculatorForm />
              </ProtectedRoute>
            }
          />

          {/* 3. College Compare */}
          <Route
            path={ROUTES.COLLEGE_COMPARE}
            element={
              <ProtectedRoute>
                <College />
              </ProtectedRoute>
            }
          />
          {/* 3.1 College Finder Dashboard */}
          <Route
            path={ROUTES.COLLEGES}
            element={
              <ProtectedRoute>
                <CollegeDashboard />
              </ProtectedRoute>
            }
          />

          {/* 4. Finance routes */}
          <Route
            path={ROUTES.FINANCE}
            element={
              <ProtectedRoute>
                <Finance />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.FINANCE_FLOW}
            element={
              <ProtectedRoute>
                <FinanceFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.LOAN_CALCULATOR}
            element={<StudentLoanCalculator />}
          />
          <Route
            path={ROUTES.LOAN_ELIGIBILITY}
            element={
              <ProtectedRoute>
                <LoanEligibility />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DOCUMENT_UPLOAD}
            element={
              <ProtectedRoute>
                <DocumentUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PHONE}
            element={
              <ProtectedRoute>
                <Phone />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.FINANCE_SCHOLARSHIP}
            element={
              <ProtectedRoute>
                <Scholarship />
              </ProtectedRoute>
            }
          />

          {/* 5. OneGrad AI */}
          <Route
            path={ROUTES.CHAT_BOT}
            element={
              <ProtectedRoute>
                <ChatBot />
              </ProtectedRoute>
            }
          />

          {/* 6. OG Connect */}
          <Route
            path={ROUTES.CONNECT_OG}
            element={
              <ProtectedRoute>
                <ConnectOG />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CONNECT_OG_FILTERS}
            element={
              <ProtectedRoute>
                <FiltersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CONNECT_OG_PROFILES}
            element={
              <ProtectedRoute>
                <ProfilesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CONNECT_OG_PROFILE_DETAILS}
            element={
              <ProtectedRoute>
                <ProfileDetailsPage />
              </ProtectedRoute>
            }
          />

          {/* 7. User Profile */}
          <Route
            path={ROUTES.USER_PROFILE}
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* 8. Payment */}
          <Route
            path={ROUTES.PAYMENT}
            element={
              <ProtectedRoute>
                <Payment />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.STRIPE_PAYMENT_PROCESSING}
            element={<StripePaymentProcessing />}
          />
          <Route
            path={ROUTES.PHONEPE_PAYMENT_PROCESSING}
            element={<PhonePePaymentProcessing />}
          />
          <Route path={ROUTES.PAYMENT_FAILED} element={<PaymentFailed />} />
        </Routes>
      </Suspense>
    </>
  );
};
